import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, DollarSign, ArrowUpCircle, ArrowDownCircle, PieChart as PieChartIcon, BarChart3, FileDown, Loader2, Building2, Calendar, Filter } from 'lucide-react';
import financeService from '../services/finance';
import cabangService from '../services/cabang';
import usePdfExport from '../hooks/usePdfExport';

const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>{entry.name}: <span className="font-bold">{formatRupiah(entry.value)}</span></p>
        ))}
      </div>
    );
  }
  return null;
};

const LaporanAkhir = () => {
  const [laporanData, setLaporanData] = useState(null);
  const [cabangList, setCabangList] = useState([]);
  const [cabangFilter, setCabangFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const { exporting, exportPdf, contentRef } = usePdfExport({
    filename: 'laporan-keuangan',
    scale: 3, orientation: 'landscape'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (cabangFilter !== 'All') params.cabangId = cabangFilter;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      const data = await financeService.getLaporan(params);
      setLaporanData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }    }, [cabangFilter, dateRange]);

  const fetchCabang = useCallback(async () => {
    try {
      const data = await cabangService.getCabang();
      setCabangList(data.cabang || []);
    } catch (err) { }
  }, []);

  useEffect(() => { fetchData(); fetchCabang(); }, [fetchData, fetchCabang]);

  const summary = laporanData?.summary || { totalPemasukan: 0, totalPengeluaran: 0, saldoAkhir: 0, countPemasukan: 0, countPengeluaran: 0 };
  const byType = laporanData?.byType || [];
  const monthlyData = laporanData?.monthly || [];
  const accountSummary = laporanData?.accountSummary || [];

  // Chart data preparation
  const monthlyChartData = useMemo(() => {
    const months = [...new Set(monthlyData.map(m => `${m.year}-${String(m.month).padStart(2, '0')}`))].sort();
    return months.map(m => {
      const [y, mo] = m.split('-');
      const pemasukan = monthlyData.find(d => d.year === Number(y) && d.month === Number(mo) && d.tipe === 'PEMASUKAN');
      const pengeluaran = monthlyData.find(d => d.year === Number(y) && d.month === Number(mo) && d.tipe === 'PENGELUARAN');
      return {
        month: format(new Date(Number(y), Number(mo) - 1), 'MMM yyyy'),
        Pemasukan: pemasukan?.total || 0,
        Pengeluaran: pengeluaran?.total || 0
      };
    });
  }, [monthlyData]);

  const categoryData = useMemo(() => {
    return byType.map(b => ({ name: `${b.tipe} - ${b.kategori}`, value: b.total, type: b.tipe }));
  }, [byType]);

  const COLORS = ['#10B981', '#34D399', '#6EE7B7', '#EF4444', '#F87171', '#FCA5A5'];
  const PIE_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-4 animate-fade-in" ref={contentRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Laporan Akhir Keuangan</h2>
          <p className="mt-1 text-sm text-gray-500">Comprehensive financial report combining income and expenses</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => exportPdf('Laporan-Keuangan')} disabled={exporting} className="btn-primary text-sm">
            {exporting ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Exporting...</span></> : <><FileDown className="w-4 h-4" /><span>Export PDF</span></>}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="px-4 py-3">
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select value={cabangFilter} onChange={e => setCabangFilter(e.target.value)} className="select-field text-sm">
              <option value="All">All Cabang</option>
              {cabangList.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">Dari:</label>
              <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} className="input-field text-sm" />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">Sampai:</label>
              <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} className="input-field text-sm" />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-20"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /><span className="mt-4 text-gray-500">Loading report...</span></div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-500">Total Pemasukan</p><p className="text-xl font-bold text-green-600 mt-1">{formatRupiah(summary.totalPemasukan)}</p></div>
                <div className="p-3 rounded-xl bg-green-50"><ArrowUpCircle className="w-6 h-6 text-green-600" /></div>
              </div>
              <p className="text-xs text-gray-400 mt-1">{summary.countPemasukan} transaksi</p>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-gray-500">Total Pengeluaran</p><p className="text-xl font-bold text-red-600 mt-1">{formatRupiah(summary.totalPengeluaran)}</p></div>
                <div className="p-3 rounded-xl bg-red-50"><ArrowDownCircle className="w-6 h-6 text-red-600" /></div>
              </div>
              <p className="text-xs text-gray-400 mt-1">{summary.countPengeluaran} transaksi</p>
            </div>
            <div className="card p-4 col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Saldo Akhir</p>
                  <p className={`text-2xl font-bold mt-1 ${summary.saldoAkhir >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{formatRupiah(summary.saldoAkhir)}</p>
                </div>
                <div className={`p-3 rounded-xl ${summary.saldoAkhir >= 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                  <DollarSign className={`w-6 h-6 ${summary.saldoAkhir >= 0 ? 'text-blue-600' : 'text-red-600'}`} />
                </div>
              </div>
              <div className="mt-2 w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div className={`h-2 rounded-full transition-all ${summary.saldoAkhir >= 0 ? 'bg-blue-500' : 'bg-red-500'}`}
                  style={{ width: `${summary.totalPemasukan > 0 ? Math.min(Math.abs(summary.saldoAkhir) / summary.totalPemasukan * 100, 100) : 0}%` }} />
              </div>
            </div>
          </div>

          {/* Account Summary Table */}
          {accountSummary.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">Detail Per Akun</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Kode</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Nama Akun</th>
                      <th className="px-4 py-2 text-center text-xs font-semibold text-gray-500 uppercase">Tipe</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Saldo Awal</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Pemasukan</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Pengeluaran</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Saldo Akhir</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-50">
                    {accountSummary.map(acc => (
                      <tr key={acc._id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-xs font-mono text-gray-500">{acc.kodeAkun}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">{acc.namaAkun}</td>
                        <td className="px-4 py-2 text-center"><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{acc.tipeAkun}</span></td>
                        <td className="px-4 py-2 text-right text-sm text-gray-600">{formatRupiah(acc.saldoAwal)}</td>
                        <td className="px-4 py-2 text-right text-sm font-semibold text-green-600">{formatRupiah(acc.totalPemasukan)}</td>
                        <td className="px-4 py-2 text-right text-sm font-semibold text-red-600">{formatRupiah(acc.totalPengeluaran)}</td>
                        <td className="px-4 py-2 text-right text-sm font-bold text-blue-600">{formatRupiah(acc.saldoAkhir)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td colSpan="3" className="px-4 py-2 text-right text-sm text-gray-700">Total</td>
                      <td className="px-4 py-2 text-right text-sm text-gray-700">{formatRupiah(accountSummary.reduce((s, a) => s + (a.saldoAwal || 0), 0))}</td>
                      <td className="px-4 py-2 text-right text-sm text-green-600">{formatRupiah(accountSummary.reduce((s, a) => s + a.totalPemasukan, 0))}</td>
                      <td className="px-4 py-2 text-right text-sm text-red-600">{formatRupiah(accountSummary.reduce((s, a) => s + a.totalPengeluaran, 0))}</td>
                      <td className="px-4 py-2 text-right text-sm text-blue-600">{formatRupiah(accountSummary.reduce((s, a) => s + a.saldoAkhir, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Kategori Distribution */}
            {categoryData.length > 0 && (
              <div className="card">
                <div className="card-header"><h3 className="section-title">Distribusi per Kategori</h3></div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={categoryData} barSize={40} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={150} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                        {categoryData.map((entry, i) => <Cell key={i} fill={entry.type === 'PEMASUKAN' ? '#10B981' : '#EF4444'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Monthly Trend */}
            {monthlyChartData.length > 0 && (
              <div className="card">
                <div className="card-header"><h3 className="section-title">Trend Bulanan</h3></div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="Pemasukan" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Pengeluaran" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          {/* Empty State */}
          {!laporanData && (
            <div className="card p-12 text-center">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No financial data available</p>
              <p className="text-gray-400 text-sm mt-1">Add transactions to see financial reports</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LaporanAkhir;
